import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Button } from '../ui/button';
import { NotificationCenter } from '../NotificationCenter';
import WalletTierBadge from '../WalletTierBadge';
import { ChevronDown } from 'lucide-react';

export function NavbarWalletSection({ b4pBalance }) {
    const { t } = useTranslation();
    return (
        <ConnectButton.Custom>
            {({ account, chain, openAccountModal, openChainModal, openConnectModal, mounted }) => {
                const ready = mounted;
                const connected = ready && account && chain;
                return (
                    <div {...(!ready && { 'aria-hidden': true, style: { opacity: 0, pointerEvents: 'none', userSelect: 'none' } })}>
                        {!connected && (
                            <Button
                                onClick={openConnectModal}
                                className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] hover:shadow-[0_0_20px_rgba(130,255,122,0.3)] font-semibold"
                                data-testid="connect-wallet-button"
                            >
                                {t('nav.connectWallet')}
                            </Button>
                        )}
                        {connected && chain.unsupported && (
                            <div className="flex items-center gap-2">
                                <Button
                                    onClick={openChainModal}
                                    className="bg-red-500 text-white hover:bg-red-600 text-sm animate-pulse"
                                    data-testid="wrong-network-btn"
                                    title={t('netswitch.title')}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5">
                                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z" />
                                        <path d="M12 9v4" />
                                        <path d="M12 17h.01" />
                                    </svg>
                                    {t('nav.wrongNetwork')}
                                </Button>
                            </div>
                        )}
                        {connected && !chain.unsupported && (
                            <div className="flex items-center gap-1 sm:gap-2">
                                {b4pBalance !== null && (
                                    <Link
                                        to="/b4p"
                                        className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/20 hover:bg-[#FFD700]/20 transition-colors"
                                        data-testid="navbar-b4p-balance"
                                    >
                                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FF6B00] flex items-center justify-center text-[8px] font-bold text-white">B</div>
                                        <span className="text-[#FFD700] text-sm font-semibold">{b4pBalance.toLocaleString()}</span>
                                    </Link>
                                )}
                                <WalletTierBadge address={account.address} />
                                <NotificationCenter />

                                <button
                                    onClick={openChainModal}
                                    className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors shrink-0"
                                >
                                    {chain.hasIcon && (
                                        <div className="w-5 h-5 rounded-full overflow-hidden" style={{ background: chain.iconBackground }}>
                                            {chain.iconUrl && <img alt={chain.name ?? 'Chain icon'} src={chain.iconUrl} className="w-5 h-5" />}
                                        </div>
                                    )}
                                    <span className="text-white text-sm hidden sm:inline">{chain.name}</span>
                                </button>

                                <button
                                    onClick={openAccountModal}
                                    className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[#00FF66]/30 transition-colors shrink-0"
                                    data-testid="wallet-dropdown-trigger"
                                >
                                    <div className="w-2 h-2 rounded-full bg-[#00FF66] animate-pulse shrink-0" />
                                    <div className="text-left whitespace-nowrap">
                                        <div className="font-mono text-xs sm:text-sm text-white leading-tight">{account.displayName}</div>
                                        {account.displayBalance && (
                                            <div className="font-mono text-[10px] text-[#00FF66] hidden sm:block leading-tight">{account.displayBalance}</div>
                                        )}
                                    </div>
                                    <ChevronDown className="w-4 h-4 text-white/50 hidden sm:block shrink-0" />
                                </button>
                            </div>
                        )}
                    </div>
                );
            }}
        </ConnectButton.Custom>
    );
}
